/**
 * Person and per-cat restlessness tick math.
 *
 * See docs/PLAN.md §4.9 (pressure contribution) and §1 (twin systems).
 */

import type { RunConfig } from './run-config.js';
import type { CatGameplayState } from './soothing.js';
import { createCatGameplayState, updatePurringFlag } from './soothing.js';
import type { CatArchetype, CatState, ImpactEvent } from './types.js';

/** Advance sleeper restlessness for one fixed step. */
export function tickPersonRestlessness(
  current: number,
  purrField: number,
  dtSeconds: number,
  config: RunConfig,
): number {
  const rise = config.personBaseRatePerSecond - purrField * config.purrDampeningPerSecond;
  return clamp01(current + rise * dtSeconds);
}

/** Pressure-driven restlessness rate for one cat (per second). */
export function pressureRestlessnessRate(pressureRatio: number, config: RunConfig): number {
  const over = Math.max(0, pressureRatio - config.pressureRestlessnessThreshold);
  return over * over * config.pressureRestlessnessGainPerSecond;
}

/** One-shot restlessness bump from an impact on this cat. */
export function impactRestlessnessBump(impulse: number, config: RunConfig): number {
  return Math.min(0.5, impulse * config.impactRestlessnessPerImpulse);
}

/**
 * Update per-cat restlessness from pressure and decaying impact impulses.
 */
export function tickCatRestlessness(
  states: readonly CatState[],
  archetypes: ReadonlyMap<string, CatArchetype>,
  catPlay: Map<string, CatGameplayState>,
  config: RunConfig,
  dtSeconds: number,
): void {
  for (const state of states) {
    const archetype = archetypes.get(state.archetypeId);
    if (!archetype) continue;

    let entry = catPlay.get(state.catId);
    if (!entry) {
      entry = createCatGameplayState();
      catPlay.set(state.catId, entry);
    }
    if (entry.leaving) continue;

    const pressureGain = pressureRestlessnessRate(state.pressureRatio, config) * dtSeconds;
    const impactGain =
      state.lastImpactImpulse > 0 ? impactRestlessnessBump(state.lastImpactImpulse, config) : 0;

    entry.restlessness = clamp01(entry.restlessness + pressureGain + impactGain);
    updatePurringFlag(entry, state.pressureRatio, config);
  }

  const alive = new Set(states.map((s) => s.catId));
  for (const id of catPlay.keys()) {
    if (!alive.has(id)) catPlay.delete(id);
  }
}

/** Apply impact events as extra restlessness on the impacted bodies. */
export function applyImpactRestlessness(
  events: readonly ImpactEvent[],
  catPlay: Map<string, CatGameplayState>,
  config: RunConfig,
): void {
  for (const ev of events) {
    if (ev.lowerCatId === 'sleeper') continue;
    const entry = catPlay.get(ev.lowerCatId);
    if (!entry || entry.leaving) continue;
    entry.restlessness = clamp01(entry.restlessness + impactRestlessnessBump(ev.impulse, config));
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
